/**
 * PiataVinde — publicare / editare produs (listare 100% gratuită).
 */
import { useEffect, useState, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Loader2, ImagePlus, X, CreditCard, HandCoins, BadgePercent,
  Store, CheckCircle2, LockKeyhole,
} from 'lucide-react';
import { toast } from 'sonner';
import PiataHeader from '../components/PiataHeader';
import SiteFooter from '../components/SiteFooter';
import useSEO from '../hooks/useSEO';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { PIATA_CATEGORIES, imgUrl, UNITS } from '../lib/piata';

const EMPTY = {
  title: '', description: '', category: 'legume-fructe', price_ron: '', unit: 'buc',
  stock: 1, location: '', images: [], payment_methods: ['card', 'direct'],
  seller_phone: '', seller_iban: '',
};

export default function PiataVinde() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('edit');
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useSEO({
    title: 'Vinde gratuit · Piața micilor producători · Energy Project Design',
    description: 'Publică-ți produsele gratuit pe piața micilor producători. Zero taxe de listare, comision doar 1% per tranzacție.',
    canonical: 'https://www.energyprojectdesign.com/piata/vinde',
  });

  useEffect(() => {
    if (editId && user) {
      api.get(`/piata/products/${editId}`).then(({ data }) => {
        setForm({
          title: data.title, description: data.description, category: data.category,
          price_ron: data.price_ron, unit: data.unit, stock: data.stock,
          location: data.location, images: data.images || [],
          payment_methods: data.payment_methods || ['direct'],
          seller_phone: data.seller_phone || '', seller_iban: '',
        });
      }).catch(() => toast.error('Produsul nu a putut fi încărcat'));
    }
  }, [editId, user]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleMethod = (m) => {
    setForm((f) => {
      const has = f.payment_methods.includes(m);
      const next = has ? f.payment_methods.filter((x) => x !== m) : [...f.payment_methods, m];
      return { ...f, payment_methods: next };
    });
  };

  const uploadImages = async (files) => {
    const remaining = 3 - form.images.length;
    const selected = Array.from(files).slice(0, remaining);
    if (!selected.length) return;
    setUploading(true);
    try {
      for (const file of selected) {
        const fd = new FormData();
        fd.append('file', file);
        const { data } = await api.post('/piata/upload-image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        setForm((f) => ({ ...f, images: [...f.images, data.url].slice(0, 3) }));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Eroare la încărcarea imaginii');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const submit = async () => {
    if (!form.title.trim() || form.title.trim().length < 3) return toast.error('Titlul trebuie să aibă minim 3 caractere');
    if (!form.price_ron || Number(form.price_ron) < 2) return toast.error('Prețul minim este 2 RON');
    if (!form.payment_methods.length) return toast.error('Alege cel puțin o metodă de plată');
    if (form.payment_methods.includes('card') && !editId && !form.seller_iban.trim()) {
      return toast.error('Pentru plăți cu cardul, IBAN-ul de încasare este obligatoriu');
    }
    setSaving(true);
    try {
      const payload = { ...form, price_ron: Number(form.price_ron), stock: Number(form.stock) };
      if (editId) {
        if (!payload.seller_iban) delete payload.seller_iban;
        await api.put(`/piata/products/${editId}`, payload);
        toast.success('Produsul a fost actualizat');
      } else {
        await api.post('/piata/products', payload);
        toast.success('Produsul a fost publicat gratuit!');
      }
      navigate('/piata/cont');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Eroare la salvare');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <PiataHeader active="vinde" />
        <div className="pt-32 pb-20 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-zinc-400" /></div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-white" data-testid="piata-vinde-authwall">
        <PiataHeader active="vinde" />
        <div className="pt-32 pb-20 max-w-md mx-auto px-6 text-center">
          <div className="w-14 h-14 rounded-full bg-zinc-100 border border-zinc-200 flex items-center justify-center mx-auto mb-5">
            <LockKeyhole className="w-6 h-6 text-zinc-500" />
          </div>
          <h1 className="text-2xl font-bold text-zinc-950 font-display mb-2">Autentifică-te pentru a vinde</h1>
          <p className="text-sm text-zinc-600 leading-relaxed mb-6">
            Publicarea produselor este <strong>100% gratuită</strong> — ai nevoie doar de un cont EPD.
            Comisionul de 1% se aplică exclusiv la vânzare.
          </p>
          <div className="flex items-center justify-center gap-3">
            <Link to="/auth?mode=signup" className="epd-btn text-sm py-2" data-testid="piata-vinde-signup">Creează cont gratuit</Link>
            <Link to="/auth?mode=signin" className="border border-zinc-300 hover:border-zinc-950 rounded-md px-4 py-2 text-sm font-semibold text-zinc-700 transition-colors" data-testid="piata-vinde-signin">Autentificare</Link>
          </div>
        </div>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-zinc-900" data-testid="page-piata-vinde">
      <PiataHeader active="vinde" />

      <div className="pt-24 pb-16 max-w-3xl mx-auto px-6">
        <div className="text-[10px] uppercase tracking-[0.32em] text-zinc-500 font-bold mb-2">// listare gratuită · fără abonament · comision doar la vânzare</div>
        <h1 className="text-3xl lg:text-4xl font-bold tracking-[-0.03em] text-zinc-950 font-display mb-3">
          {editId ? 'Editează produsul' : 'Publică un produs'} <span className="italic text-zinc-400 font-normal">gratuit.</span>
        </h1>

        <div className="flex items-center gap-2 mb-8 flex-wrap">
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-full px-3 py-1"><CheckCircle2 className="w-3.5 h-3.5" /> 0 RON taxă de listare</span>
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold bg-violet-50 text-violet-800 border border-violet-200 rounded-full px-3 py-1"><BadgePercent className="w-3.5 h-3.5" /> Comision 1% / tranzacție</span>
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold bg-zinc-100 text-zinc-700 border border-zinc-200 rounded-full px-3 py-1"><Store className="w-3.5 h-3.5" /> 99% rămâne la tine</span>
        </div>

        <div className="space-y-5">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold block mb-1.5">Titlu produs *</label>
            <input value={form.title} onChange={(e) => set('title', e.target.value)} placeholder="ex: Miere de salcâm 2025, borcan 950g" className="w-full border border-zinc-300 rounded-md px-3 py-2.5 text-sm focus:border-zinc-950 outline-none" data-testid="vinde-title" />
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold block mb-1.5">Categorie *</label>
              <select value={form.category} onChange={(e) => set('category', e.target.value)} className="w-full border border-zinc-300 rounded-md px-3 py-2.5 text-sm focus:border-zinc-950 outline-none bg-white" data-testid="vinde-category">
                {PIATA_CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold block mb-1.5">Localitate / Județ</label>
              <input value={form.location} onChange={(e) => set('location', e.target.value)} placeholder="ex: Sibiu, jud. Sibiu" className="w-full border border-zinc-300 rounded-md px-3 py-2.5 text-sm focus:border-zinc-950 outline-none" data-testid="vinde-location" />
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold block mb-1.5">Descriere</label>
            <textarea value={form.description} onChange={(e) => set('description', e.target.value)} rows={4} placeholder="Povestea produsului: ingrediente, mod de producție, cantități disponibile, livrare..." className="w-full border border-zinc-300 rounded-md px-3 py-2.5 text-sm focus:border-zinc-950 outline-none" data-testid="vinde-description" />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold block mb-1.5">Preț (RON) *</label>
              <input type="number" min="2" step="0.5" value={form.price_ron} onChange={(e) => set('price_ron', e.target.value)} className="w-full border border-zinc-300 rounded-md px-3 py-2.5 text-sm focus:border-zinc-950 outline-none tabular-nums" data-testid="vinde-price" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold block mb-1.5">Unitate</label>
              <select value={form.unit} onChange={(e) => set('unit', e.target.value)} className="w-full border border-zinc-300 rounded-md px-3 py-2.5 text-sm focus:border-zinc-950 outline-none bg-white" data-testid="vinde-unit">
                {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold block mb-1.5">Stoc disponibil</label>
              <input type="number" min="0" value={form.stock} onChange={(e) => set('stock', e.target.value)} className="w-full border border-zinc-300 rounded-md px-3 py-2.5 text-sm focus:border-zinc-950 outline-none tabular-nums" data-testid="vinde-stock" />
            </div>
          </div>

          {/* Imagini */}
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold block mb-1.5">Fotografii produs (max 3, până la 3 MB fiecare)</label>
            <div className="flex gap-3 flex-wrap">
              {form.images.map((img, i) => (
                <div key={img} className="relative w-24 h-24 rounded-md overflow-hidden border border-zinc-200 group" data-testid={`vinde-image-${i}`}>
                  <img src={imgUrl(img)} alt="" className="w-full h-full object-cover" />
                  <button onClick={() => set('images', form.images.filter((x) => x !== img))} className="absolute top-1 right-1 w-5 h-5 rounded-full bg-zinc-950/80 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`vinde-image-remove-${i}`}>
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
              {form.images.length < 3 && (
                <button onClick={() => fileRef.current?.click()} disabled={uploading} className="w-24 h-24 rounded-md border-2 border-dashed border-zinc-300 hover:border-zinc-950 flex flex-col items-center justify-center gap-1 text-zinc-400 hover:text-zinc-950 transition-colors" data-testid="vinde-upload-btn">
                  {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <ImagePlus className="w-5 h-5" strokeWidth={1.5} />}
                  <span className="text-[9px] font-semibold uppercase tracking-wider">{uploading ? 'Se încarcă' : 'Adaugă'}</span>
                </button>
              )}
              <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => uploadImages(e.target.files)} data-testid="vinde-file-input" />
            </div>
          </div>

          {/* Metode de plată */}
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold block mb-1.5">Metode de plată acceptate *</label>
            <div className="grid sm:grid-cols-2 gap-2">
              <button onClick={() => toggleMethod('card')} className={`flex items-start gap-2.5 border rounded-md p-3 text-left transition-all ${form.payment_methods.includes('card') ? 'border-zinc-950 bg-zinc-950 text-white' : 'border-zinc-300 hover:border-zinc-500'}`} data-testid="vinde-method-card">
                <CreditCard className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={1.5} />
                <div>
                  <div className="text-xs font-bold">Plată cu cardul (Stripe)</div>
                  <div className={`text-[10px] ${form.payment_methods.includes('card') ? 'text-zinc-300' : 'text-zinc-500'}`}>Cumpărătorul plătește online · încasezi 99% în cont</div>
                </div>
              </button>
              <button onClick={() => toggleMethod('direct')} className={`flex items-start gap-2.5 border rounded-md p-3 text-left transition-all ${form.payment_methods.includes('direct') ? 'border-zinc-950 bg-zinc-950 text-white' : 'border-zinc-300 hover:border-zinc-500'}`} data-testid="vinde-method-direct">
                <HandCoins className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={1.5} />
                <div>
                  <div className="text-xs font-bold">Comandă directă</div>
                  <div className={`text-[10px] ${form.payment_methods.includes('direct') ? 'text-zinc-300' : 'text-zinc-500'}`}>Plata la livrare / transfer · comision 1% înregistrat</div>
                </div>
              </button>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold block mb-1.5">Telefon contact *</label>
              <input value={form.seller_phone} onChange={(e) => set('seller_phone', e.target.value)} placeholder="07xx xxx xxx" className="w-full border border-zinc-300 rounded-md px-3 py-2.5 text-sm focus:border-zinc-950 outline-none" data-testid="vinde-phone" />
            </div>
            {form.payment_methods.includes('card') && (
              <div>
                <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold block mb-1.5">IBAN încasări {editId ? '(gol = nemodificat)' : '*'}</label>
                <input value={form.seller_iban} onChange={(e) => set('seller_iban', e.target.value)} placeholder="RO49 AAAA 1B31..." className="w-full border border-zinc-300 rounded-md px-3 py-2.5 text-sm focus:border-zinc-950 outline-none font-mono" data-testid="vinde-iban" />
              </div>
            )}
          </div>

          <button onClick={submit} disabled={saving} className="epd-btn w-full text-sm py-3" data-testid="vinde-submit">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Store className="w-4 h-4" />}
            {editId ? 'Salvează modificările' : 'Publică gratuit'}
          </button>
          <p className="text-[10px] text-zinc-500 text-center leading-relaxed">
            Publicarea este gratuită și instantanee. La fiecare vânzare, EPD reține 1% comision — restul de 99% îți revine integral.
            Pentru plățile cu cardul, sumele nete se virează în IBAN-ul tău.
          </p>
        </div>
      </div>

      <SiteFooter />
    </div>
  );
}
